import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SlideFrame } from '../SlideFrame';
import { operatingSteps } from '../../data/deckContent';

/**
 * The pivot slide: the deck stops arguing the idea and starts running it.
 * The chapter marker hands over from strategy to tactics, and the process
 * becomes a looping delivery track rather than a row of cards.
 */
export function OperatingModelSlide() {
  const reduced = useReducedMotion();
  return <SlideFrame eyebrow="The operating model" index={7} chapter="tactics" data-id="element-96" data-mp="29j2xu2uvkznb">
      <div data-id="element-97" data-mp="24ix84s861n7v">
        {/* strategy → tactics handover */}
        <div className="flex items-center gap-[1.4vw] border-b border-white/12 pb-[1.6vh]" data-id="element-98" data-mp="1zqnasr4q1kvk">
          <span className="text-[clamp(8px,0.8vw,15px)] font-medium uppercase tracking-[0.3em] text-white/25 line-through decoration-white/20" data-id="element-99" data-mp="1vqd4fdd0osg6">
            Part one · the strategy
          </span>
          <span aria-hidden="true" className="text-[clamp(10px,1vw,19px)] text-[color:var(--sv-green)]/70" data-id="element-100" data-mp="2nghn97l4p7fa">
            →
          </span>
          <span className="rounded-full border border-[color:var(--sv-green)]/50 bg-[color:var(--sv-green)]/10 px-[1vw] py-[0.6vh] text-[clamp(8px,0.8vw,15px)] font-semibold uppercase tracking-[0.3em] text-[color:var(--sv-green)]" data-id="element-101" data-mp="2iir3hhrcohuy">
            Part two · the tactics
          </span>
        </div>

        <div className="mt-[3vh] flex items-end justify-between gap-[3vw]" data-id="element-102" data-mp="1ut679x66ro3q">
          <h2 className="text-[clamp(24px,3.4vw,60px)] font-semibold leading-[1.06] tracking-tight text-white" data-id="element-103" data-mp="1urv707by8yhd">
            Now we make it real.
          </h2>
          <p className="max-w-[34ch] shrink-0 text-[clamp(10px,1.05vw,20px)] font-light leading-snug text-white/50" data-id="element-104" data-mp="1kvrzsn28q9nk">
            Not one campaign. A planned rhythm of valuable Solventum
            interactions.
          </p>
        </div>

        {/* delivery track */}
        <div className="relative mt-[5vh]" data-id="element-105" data-mp="3k7ujnyes161r">
          <div aria-hidden="true" className="absolute left-0 right-0 top-[0.55vh] h-[1px] bg-white/12" data-id="element-106" data-mp="3k6ta5ydesrm5" />

          <ol className="relative grid grid-cols-7" data-id="element-107" data-mp="3iokuvkufzgaw">
            {operatingSteps.map((step, i) => {
            const isLast = i === operatingSteps.length - 1;
            return <motion.li key={step.label} initial={reduced ? false : {
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.32,
              delay: 0.05 * i,
              ease: [0.23, 1, 0.32, 1]
            }} className="flex flex-col pr-[1vw]" data-id="element-108" data-mp="38uu9iotm3lzf" data-mpi={i}>
                  <span aria-hidden="true" className={`h-[1.1vh] w-[1.1vh] rounded-full ${isLast ? 'bg-[color:var(--sv-green)] ring-[0.4vh] ring-[color:var(--sv-green)]/20' : 'bg-white/70'}`} data-id="element-109" data-mp="3nqa4qx5xhnxe" />
                  <span className="mt-[2vh] text-[clamp(7px,0.7vw,13px)] font-medium tabular-nums tracking-[0.2em] text-white/30" data-id="element-110" data-mp="21zvbd7khjoa3">
                    {isLast ? '↺' : `0${i + 1}`}
                  </span>
                  <span className={`mt-[0.8vh] text-[clamp(10px,1.05vw,20px)] font-semibold uppercase tracking-[0.16em] ${isLast ? 'text-[color:var(--sv-green)]' : 'text-white'}`} data-id="element-111" data-mp="2axowdmcsuvhy">
                    {step.label}
                  </span>
                  <span className="mt-[1vh] text-[clamp(8px,0.85vw,16px)] font-light leading-snug text-white/45" data-id="element-112" data-mp="2puehvxpq91c0">
                    {step.detail}
                  </span>
                </motion.li>;
          })}
          </ol>

          {/* the loop back to the start */}
          <svg aria-hidden="true" viewBox="0 0 100 8" preserveAspectRatio="none" className="mt-[2.2vh] h-[4.5vh] w-full" data-id="element-113" data-mp="33pqob2d8wg8r">
            <path d="M 99 0 C 99 7, 92 7.5, 50 7.5 C 8 7.5, 1 7, 1 0" fill="none" stroke="var(--sv-green)" strokeOpacity="0.45" strokeWidth="0.18" vectorEffect="non-scaling-stroke" data-id="element-114" data-mp="2uyyulm5mdeuz" />
            <path d="M 1 0 l 1.2 1.6 M 1 0 l -1.2 1.6" fill="none" stroke="var(--sv-green)" strokeOpacity="0.7" strokeWidth="0.2" vectorEffect="non-scaling-stroke" data-id="element-115" data-mp="2zqqddaijagak" />
          </svg>
        </div>

        <p className="mt-[2.4vh] text-[clamp(13px,1.6vw,30px)] font-light text-white" data-id="element-116" data-mp="2m04uhlt2y6co">
          One recognizable format.{' '}
          <span className="font-medium text-[color:var(--sv-green)]" data-id="element-117" data-mp="2gzpd8j5ogepf">
            Many different reasons to engage.
          </span>
        </p>
      </div>
    </SlideFrame>;
}